import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { History, Loader2 } from "lucide-react";
import { useAiSuggestionLog, AiSuggestionRow } from "@/hooks/useAiSuggestionLog";

interface AiSuggestionHistoryDialogProps {
  documentType: string;
  documentId?: string | null;
  label?: string;
}

/**
 * Visar historik över AI:ns språkförslag: original, förslag och om det användes.
 */
export const AiSuggestionHistoryDialog: React.FC<AiSuggestionHistoryDialogProps> = ({
  documentType,
  documentId,
  label = "AI-historik",
}) => {
  const { fetchHistory } = useAiSuggestionLog();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AiSuggestionRow[]>([]);

  const load = async () => {
    setOpen(true);
    setLoading(true);
    setRows(await fetchHistory(documentType, documentId));
    setLoading(false);
  };

  return (
    <>
      <Button type="button" variant="ghost" size="sm" onClick={load}>
        <History className="h-4 w-4 mr-1.5" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>AI-historik</DialogTitle>
            <DialogDescription>
              Här ser du vad AI:n föreslog, vad originaltexten var och om förslaget användes.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Ingen AI-historik ännu för det här dokumentet.
            </p>
          ) : (
            <ScrollArea className="max-h-[60vh] pr-3">
              <div className="space-y-3">
                {rows.map((r) => (
                  <div key={r.id} className="rounded-md border p-3 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{r.field_label || r.field_key}</span>
                      <Badge variant={r.accepted ? "default" : "outline"}>
                        {r.accepted ? "Använt" : "Ej använt"}
                      </Badge>
                      {r.source === "document_review" && (
                        <Badge variant="secondary">Dokumentgranskning</Badge>
                      )}
                      <span className="text-xs text-muted-foreground ml-auto">
                        {new Date(r.created_at).toLocaleString("sv-SE")}
                      </span>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Original</p>
                        <p className="text-sm whitespace-pre-wrap">{r.original_text}</p>
                      </div>
                      <div className="rounded-md bg-muted/50 p-2">
                        <p className="text-xs text-muted-foreground mb-1">AI-förslag</p>
                        <p className="text-sm whitespace-pre-wrap">{r.suggested_text}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AiSuggestionHistoryDialog;
